/**
 *
 * @module product
 *
 */

import Check from 'check-types';

import Exception from './../exception/entry';

import Campaign from './campaign';
import IntroPrice from './introPrice';

const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    array,
    nonEmptyString,
    instanceStrict,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class Product
 * @desc The available product information.
 *
 */
export default class Product {

    /**
     *
     * @param {Object} options
     * @param {SDK.Services.Paywall.Campaign} [options.campaign]
     * @param {Array<String>} options.entitlements
     * @param {Array<String>} [options.groups=[]]
     * @param {SDK.Services.Paywall.IntroPrice|null} [options.introPrice]
     * @param {String} options.name
     * @param {String<SDK.Services.Paywall.ProductType>} options.productType
     * @param {String} options.sku
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options = {}) {

        const errorMessage = {
            ctor: `${this.constructor.name}(options)`,
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get campaign() {
                return `${this.ctor} needs SDK.Services.Paywall.Campaign instance`;
            },
            get entitlements() {
                return `${this.ctor} options.entitlements should be an Array of Strings with length`;
            },
            get groups() {
                return `${this.ctor} options.groups should be an Array of Strings`;
            },
            get introPrice() {
                return `${this.ctor} needs SDK.Services.Paywall.IntroPrice instance`;
            },
            get name() {
                return `${this.ctor} options.name should be a String with length`;
            },
            get productType() {
                return `${this.ctor} options.productType should be a String with length`;
            },
            get sku() {
                return `${this.ctor} options.sku should be a String with length`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { campaign, entitlements, groups, introPrice, name, productType, sku } = options;

        if (Check.assigned(campaign)) {
            instanceStrict(campaign, Campaign, errorMessage.campaign, InvalidArgumentException);
        }

        array.of.nonEmptyString(entitlements, errorMessage.entitlements, InvalidArgumentException);

        if (Check.assigned(groups)) {
            array.of.nonEmptyString(groups, errorMessage.groups, InvalidArgumentException);
        }

        if (Check.assigned(introPrice)) {
            instanceStrict(introPrice, IntroPrice, errorMessage.introPrice, InvalidArgumentException);
        }

        nonEmptyString(name, errorMessage.name, InvalidArgumentException);
        nonEmptyString(productType, errorMessage.productType, InvalidArgumentException);
        nonEmptyString(sku, errorMessage.sku, InvalidArgumentException);

        /**
         *
         * @access public
         * @type {SDK.Services.Paywall.Campaign}
         * @desc The campaign information, will be null for IAP products.
         *
         */
        this.campaign = campaign;

        /**
         *
         * @access public
         * @type {Array<String>}
         * @desc The product entitlements.
         *
         */
        this.entitlements = entitlements;

        /**
         *
         * @access public
         * @type {Array<String>}
         * @desc Maps to product categories in commerce.
         *
         */
        this.groups = groups;

        /**
         *
         * @access public
         * @type {SDK.Services.Paywall.IntroPrice|null}
         * @desc The introductory pricing info, will be null for direct billing products.
         *
         */
        this.introPrice = introPrice;

        /**
         *
         * @access public
         * @type {String}
         * @desc The product name.
         *
         */
        this.name = name;

        /**
         *
         * @access public
         * @type {String<SDK.Services.Paywall.ProductType>}
         * @desc The product type (IAP or D2C).
         *
         */
        this.productType = productType;

        /**
         *
         * @access public
         * @type {String}
         * @desc The product sku.
         *
         */
        this.sku = sku;

    }

}



// WEBPACK FOOTER //
// ./src/services/paywall/product.js